import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;
import { XMarkIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { SavedView } from '../types';

interface SavedViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description: string; is_public: boolean }) => void;
  isLoading?: boolean;
  initialName?: string;
  initialDescription?: string;
  initialIsPublic?: boolean;
  title?: string;
  /**
   * Current cached views list — used for live name-conflict detection
   * (own-duplicate + public-name-taken-by-other-user).
   */
  views?: SavedView[];
  currentUserEmail?: string | null;
  /** In edit mode, the view being edited is excluded from conflict checks. */
  currentViewId?: number;
  /**
   * Backend-side error to seed under the Name field — used when the caller
   * opened this modal in response to a 400 (e.g. "make public" conflict).
   * Live validation takes over as soon as the user starts typing.
   */
  externalNameError?: string | null;
}

export const SavedViewModal: React.FC<SavedViewModalProps> = ({
  isOpen,
  onClose,
  onSave,
  isLoading = false,
  initialName = '',
  initialDescription = '',
  initialIsPublic = false,
  title = 'Save View',
  views = [],
  currentUserEmail = null,
  currentViewId,
  externalNameError = null,
}) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [isPublic, setIsPublic] = useState(initialIsPublic);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setDescription(initialDescription);
      setIsPublic(initialIsPublic);
      setDirty(false);
    }
  }, [isOpen, initialName, initialDescription, initialIsPublic]);

  const liveNameError = useMemo<string | null>(() => {
    const trimmed = name.trim();
    if (!trimmed) return null; // empty handled on submit, not live
    let ownClash = false;
    let publicClash = false;
    for (const v of views) {
      if (v.id === currentViewId || v.name !== trimmed) continue;
      if (v.author_email === currentUserEmail) ownClash = true;
      if (isPublic && v.is_public) publicClash = true;
      if (ownClash && (!isPublic || publicClash)) break;
    }
    if (ownClash) return 'You already have a view with this name.';
    if (publicClash) return 'A public view with this name already exists.';
    return null;
  }, [name, isPublic, views, currentUserEmail, currentViewId]);

  // Show backend error until the user touches the form; then live validation
  // is authoritative (prevents a stale 400 from sticking after a fix).
  //
  // Suppress the error row entirely while a save is in flight OR while the
  // modal is closing: on create, tanstack's onSuccess synchronously inserts
  // the new view into the cached list *before* handleModalSave's awaited
  // closeModal() runs, so the modal briefly re-renders with `views`
  // containing its own fresh row — triggering a false "You already have a
  // view with this name." flash. The `!isOpen` clause also covers the
  // headless-ui leave animation: children stay mounted while fading out, so
  // without this guard the same clash text would be visible for the ~200ms
  // of the close transition. Save button is disabled by isLoading regardless,
  // so hiding the row here is lossless.
  const displayNameError = (isLoading || !isOpen)
    ? null
    : liveNameError ?? (dirty ? null : externalNameError);
  const emptyName = !name.trim();
  const submitDisabled = isLoading || !!liveNameError || emptyName;

  const handleSave = () => {
    if (submitDisabled) return;
    onSave({ name: name.trim(), description: description.trim(), is_public: isPublic });
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <TransitionChild
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-[101] overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-10">
            <TransitionChild
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <DialogTitle as="h3" className="text-lg font-semibold text-gray-900">
                    {title}
                  </DialogTitle>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (!dirty) setDirty(true);
                      }}
                      className={clsx(
                        'w-full px-3 py-2 text-sm border rounded-md focus:ring-blue-500 focus:border-blue-500',
                        displayNameError ? 'border-red-300' : 'border-gray-300'
                      )}
                      placeholder="View name"
                      maxLength={100}
                      autoFocus
                    />
                    {displayNameError && (
                      <p className="mt-1 text-xs text-red-600">{displayNameError}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional description"
                      rows={2}
                      maxLength={500}
                    />
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <div>
                      <span className="text-sm font-medium text-gray-900">Share with team</span>
                      <p className="text-xs text-gray-500">Make visible to all users</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsPublic(!isPublic);
                        if (!dirty) setDirty(true);
                      }}
                      className={clsx(
                        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                        isPublic ? 'bg-blue-600' : 'bg-gray-200'
                      )}
                    >
                      <span
                        className={clsx(
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                          isPublic ? 'translate-x-5' : 'translate-x-0'
                        )}
                      />
                    </button>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 bg-gray-50">
                  <button
                    onClick={onClose}
                    className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={submitDisabled}
                    className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
